/**
 * useUser Composable
 * 提供用户相关的响应式状态和方法
 */

import { computed, type ComputedRef } from '@vue/reactivity'
import type { UserService } from '../../../core/services/UserService'
import type {
  UserInfo,
  VipInfo,
  PointsInfo,
  TempPointListParams,
  TempPointListResult,
  TempPointItem,
  PointsLogParams,
  PointsLogResult,
  Task,
  FeedbackParams,
  SignInResponse
} from '../../../types'

export interface UseUserReturn {
  // 响应式状态
  userInfo: ComputedRef<UserInfo | null>
  vipInfo: ComputedRef<VipInfo | null>
  pointsInfo: ComputedRef<PointsInfo | null>
  tasks: ComputedRef<Task[]>
  isVip: ComputedRef<boolean>
  isVipExpired: ComputedRef<boolean>
  vipRemainingDays: ComputedRef<number>
  availablePoints: ComputedRef<number>

  // 方法
  getUserInfo: () => Promise<UserInfo>
  getVipInfo: () => Promise<VipInfo>
  getPointsInfo: () => Promise<PointsInfo>
  getTempPointList: (params?: TempPointListParams) => Promise<TempPointListResult>
  getTempPointDetail: (id: number) => Promise<TempPointItem>
  getPointsLog: (params?: PointsLogParams) => Promise<PointsLogResult>
  getTempPointLogs: (params?: PointsLogParams) => Promise<PointsLogResult>
  getTasks: () => Promise<Task[]>
  signIn: () => Promise<SignInResponse>
  feedback: (params: FeedbackParams) => Promise<void>
  refreshAll: () => Promise<void>
  clearUserData: () => void
  bindPhone: (params: { phone: string; code: string }) => Promise<{ token?: string } | void>
  bindWechat: () => Promise<any>
  getTaskSchedule: () => Promise<any>
  getActivationCodeList: () => Promise<any>
  getUserPointGrantList: (params: Record<string, string | number>) => Promise<any>
}

/**
 * 创建 useUser composable
 */
export function createUseUser(userService: UserService) {
  return function useUser(): UseUserReturn {
    const userStore = userService.getUserStore()

    // 响应式状态（直接返回 store 的 computed，避免双重包装）
    const userInfo = computed(() => userStore.userInfo)
    const vipInfo = computed(() => userStore.vipInfo)
    const pointsInfo = computed(() => userStore.pointsInfo)
    const tasks = computed(() => userStore.tasks)
    const isVip = userStore.isVip
    const isVipExpired = userStore.isVipExpired
    const vipRemainingDays = userStore.vipRemainingDays
    const availablePoints = userStore.availablePoints

    // 方法
    const getUserInfo = async () => {
      return await userService.getUserInfo()
    }

    const getVipInfo = async () => {
      return await userService.getVipInfo()
    }

    const getPointsInfo = async () => {
      return await userService.getPointsInfo()
    }

    const getTempPointList = async (params: TempPointListParams = {}) => {
      return await userService.getTempPointList(params)
    }

    const getTempPointDetail = async (id: number) => {
      return await userService.getTempPointDetail(id)
    }

    const getPointsLog = async (params: PointsLogParams = {}) => {
      return await userService.getPointsLog(params)
    }

    const getTempPointLogs = async (params: PointsLogParams = {}) => {
      return await userService.getTempPointLogs(params)
    }

    const getTasks = async () => {
      return await userService.getTasks()
    }

    const signIn = async () => {
      return await userService.signIn()
    }

    const feedback = async (params: FeedbackParams) => {
      await userService.feedback(params)
    }

    const refreshAll = async () => {
      await userService.refreshAll()
    }

    const clearUserData = () => {
      userService.clearUserData()
    }

    const bindPhone = async (params: { phone: string; code: string }) => {
      return await userService.bindPhone(params)
    }

    const bindWechat = async () => {
      return await userService.bindWechat()
    }

    const getTaskSchedule = async () => {
      return await userService.getTaskSchedule()
    }

    const getActivationCodeList = async () => {
      return await userService.getActivationCodeList()
    }

    const getUserPointGrantList = async (params: Record<string, string | number>) => {
      return await userService.getUserPointGrantList(params)
    }

    return {
      // 响应式状态
      userInfo,
      vipInfo,
      pointsInfo,
      tasks,
      isVip,
      isVipExpired,
      vipRemainingDays,
      availablePoints,

      // 方法
      getUserInfo,
      getVipInfo,
      getPointsInfo,
      getTempPointList,
      getTempPointDetail,
      getPointsLog,
      getTempPointLogs,
      getTasks,
      signIn,
      feedback,
      refreshAll,
      clearUserData,
      bindPhone,
      bindWechat,
      getTaskSchedule,
      getActivationCodeList,
      getUserPointGrantList
    }
  }
}
